import {
  ApiRequest,
  ClientResponse,
  MyPayment,
  Order,
  ByProjectKeyMeOrdersRequestBuilder
} from "@commercetools/platform-sdk"

import { useCommerceToolsClient } from "../commercetools/CommerceToolsHooks"
import { useRootSelector, useAppDispatch } from "../State"
import { OrderState } from "./OrderState"

export const useOrderState = (): Order | undefined =>
  useRootSelector(it => it.orderState.order)

export const useOrderService = () => {
  const dispatch = useAppDispatch()
  const client = useCommerceToolsClient()

  const expand = [
    "paymentInfo.payments[*]",
    "discountCodes[*].discountCode",
    "lineItems[*].price.discounted.discount"
  ]

  const withOrder = <T>(
    id: string,
    fn: (client: ByProjectKeyMeOrdersRequestBuilder) => ApiRequest<T>
  ): Promise<ClientResponse<T>> => {
    return client.execute(it => fn(it.me().orders()))
  }

  const getForOrderNumber = (orderNumber: string) => {
    return withOrder(orderNumber, it => {
      return it.get({
        queryArgs: {
          where: `orderNumber="${orderNumber}"`,
          expand
        }
      })
    })
      .then(it => it.body.results[0])
      .then(it => dispatch(OrderState.actions.setOrder(it)).payload)
  }

  const getOrder = (orderId: string) => {
    return withOrder(orderId, it => {
      return it.get({
        queryArgs: {
          where: `id = "${orderId}"`,
          expand
        }
      })
    }).then(it => it.body.results[0])
  }

  const getOrderForCart: (cartId: string) => Promise<Order | undefined> = (
    cartId: string
  ) => {
    return withOrder(cartId, it => {
      return it.get({
        queryArgs: {
          where: `cart(id = "${cartId}")`,
          expand
        }
      })
    }).then(it => it.body.results[0])
  }

  const getPayments: (order: Order) => Promise<MyPayment[]> = (
    order: Order
  ) => {
    if (order.paymentInfo) {
      return Promise.all(
        order.paymentInfo.payments.map(payment => getPaymentInfo(payment.id))
      )
    } else {
      return Promise.resolve([])
    }
  }

  const getPaymentInfo = (paymentId: string): Promise<MyPayment> => {
    return client
      .execute(it => it.me().payments().withId({ ID: paymentId }).get())
      .then(it => it.body)
  }

  return {
    getForOrderNumber,
    getPayments,
    getOrder,
    getOrderForCart
  }
}
